import { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ==============================|| C-8 — license upsell screen (§2.2.4) ||============================== //
// Shown when a feature/route needs a module the tenant's plan doesn't include. UI-package (i18n-free):
// callers pass already-localized nodes for the copy and an upgradeUrl. Mirrors the backend
// LicenseRequired ProblemDetails (requiredModule) — the same feature that returns 403 server-side.

export interface LicenseUpsellProps {
  /** localized heading node (default: plain English) */
  title?: ReactNode;
  /** localized explanatory node */
  description?: ReactNode;
  /** the module code the plan is missing (shown as a chip when provided) */
  requiredModule?: string;
  /** §2.2.4 upgrade link; when set the CTA becomes an anchor to it */
  upgradeUrl?: string;
  /** localized CTA label (default: "Upgrade plan") */
  upgradeLabel?: ReactNode;
}

export function LicenseUpsell({ title, description, requiredModule, upgradeUrl, upgradeLabel }: LicenseUpsellProps) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, px: 2 }}>
      <Stack spacing={2} sx={{ maxWidth: 460, textAlign: 'center', alignItems: 'center' }}>
        <Typography variant="h3">{title ?? 'This feature needs a plan upgrade'}</Typography>
        <Typography variant="body1" color="text.secondary">
          {description ?? 'Your current plan does not include this feature. Upgrade to unlock it.'}
        </Typography>
        {requiredModule && <Chip variant="outlined" color="warning" label={requiredModule} sx={{ fontFamily: 'monospace' }} />}
        {upgradeUrl && (
          <Button variant="contained" href={upgradeUrl} target="_blank" rel="noopener noreferrer">
            {upgradeLabel ?? 'Upgrade plan'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
